import type {
  FieldArray,
  FieldArrayMethodProps,
  FieldArrayPath,
  FieldValues,
  Path,
  PathValue,
  UseFieldArrayProps,
} from 'react-hook-form';
import { useFieldArray, useFormContext } from 'react-hook-form';

type KeyDepth = 'shallow' | 'deep';

const collectPaths = (row: unknown, depth: KeyDepth = 'shallow'): string[] => {
  const out: string[] = [];

  const walk = (val: object, base?: string) => {
    for (const [key, value] of Object.entries(val)) {
      const next = base ? `${base}.${key}` : key;
      // * arrays are always one field; nested objects too unless depth is 'deep'
      if (
        depth === 'deep' &&
        value &&
        typeof value === 'object' &&
        !Array.isArray(value)
      ) {
        walk(value, next);
      } else {
        out.push(next);
      }
    }
  };

  if (row && typeof row === 'object' && !Array.isArray(row)) {
    walk(row);
  }
  return out;
};

type LatitudeFieldArrayProps<
  T extends FieldValues,
  N extends FieldArrayPath<T>,
  IdKey extends string = 'id',
> = {
  name: N;
  /**
   * 'deep' if you have nested objects
   * 'shallow' if you only have top-level keys
   * @default 'shallow'
   */
  clearDepth?: KeyDepth;
} & Omit<UseFieldArrayProps<T, N, IdKey>, 'name'>;

type SoftInsertOpts = FieldArrayMethodProps & {
  /** keep the insert's dirty state instead of re-baselining it (default: false) */
  keepDirty?: boolean;
  keepTouched?: boolean;
  keepError?: boolean;
  resetRoot?: boolean;
};
export const useLatitudeFieldArray = <
  T extends FieldValues,
  N extends FieldArrayPath<T>,
  IdKey extends string = 'id',
>(
  opts: LatitudeFieldArrayProps<T, N, IdKey>,
) => {
  const { name, clearDepth = 'shallow', ...rest } = opts;
  const { clearErrors, getValues, resetField } = useFormContext<T>();

  const methods = useFieldArray<T, N, IdKey>({ name, ...rest });
  const { append, prepend, fields } = methods;

  // * Shared by softAppend / softPrepend: insert, then once RHF has registered the
  // * new rows and run its forced validation, clear that validation and re-baseline.
  const softInsert = (
    insertRow: typeof append,
    idx: number,
    row: FieldArray<T, N> | FieldArray<T, N>[],
    errorKeys: ReadonlyArray<Path<FieldArray<T, N>>> | undefined,
    {
      keepDirty = false, // <- prevent dirty from the insert itself
      keepTouched = false, // <- mark pristine after the insert
      keepError = true,
      resetRoot = true, // <- re-baseline the array to its current value
      ...opts
    }: SoftInsertOpts,
  ) => {
    const rows = Array.isArray(row) ? row : [row];
    insertRow(row, opts);

    // * defer until RHF registers fields & resolver runs
    setTimeout(() => {
      // * Clear errors on the inserted rows' paths only, not on the rest of the array.
      clearErrors(
        rows.flatMap((inserted, offset) =>
          (errorKeys ?? collectPaths(inserted, clearDepth)).map(
            (key) => `${name}.${idx + offset}.${key}` as Path<T>,
          ),
        ),
      );

      if ((!keepDirty || !keepTouched || !keepError) && resetRoot) {
        // * Re-baseline row by row: resetField on the array path itself goes through
        // * setValue, which makes useFieldArray regenerate every row id and remount
        // * every row. A row path isn't a field-array path, so its ids survive.
        const current = getValues(name as Path<T>) as unknown[];
        current.forEach((value, index) => {
          resetField(`${name}.${index}` as Path<T>, {
            defaultValue: value as PathValue<T, Path<T>>,
            keepDirty,
            keepTouched,
            keepError,
          });
        });
        // * Row resets don't reach errors on the array itself (`${name}.root`).
        if (!keepError) {
          clearErrors(name as Path<T>);
        }
      }
    }, 0);
  };

  /**
   * * Soft-append: append then clear initial errors for the new element(s).
   * * - If errorKeys are omitted, they are derived from each row (shallow by default).
   */
  const softAppend = (
    row: FieldArray<T, N> | FieldArray<T, N>[],
    errorKeys: ReadonlyArray<Path<FieldArray<T, N>>> | undefined = undefined,
    opts: SoftInsertOpts = {},
  ) => softInsert(append, fields.length, row, errorKeys, opts);

  /** Soft-prepend: softAppend's counterpart that inserts at index 0. */
  const softPrepend = (
    row: FieldArray<T, N> | FieldArray<T, N>[],
    errorKeys: ReadonlyArray<Path<FieldArray<T, N>>> | undefined = undefined,
    opts: SoftInsertOpts = {},
  ) => softInsert(prepend, 0, row, errorKeys, opts);

  return { ...methods, softAppend, softPrepend };
};
